/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { enterApp, leaveApp, loadLocalInterfacePreferences, openOnboardingWizardV13, saveLocalInterfacePreferences } from '../onboarding/onboarding.runtime.js';
import { syncAndroidAccessToken } from '../platform/android-uikit.runtime.js';
import { $, $$, API, ONBOARDING_VERSION, adminHasRole, api, authBackStage, clearAppBoot, cookie, copyText, currentGroup, downloadQRCode, enterInviteGuest, escapeHTML, hasRole, hasScopedRole, isAppBootActive, isProjectAdmin, loadAllGroupPages, loadLegalDocuments, parseBody, rawRequest, renderQRCode, replaceLocationPath, sectionDefs, setAuthError, state, superGrokMode, syncInviteStageCopy, t, toast, trackProduct } from '../platform/core.runtime.js';
import { closeDialog, openDialog, transitionDialog } from '../platform/dialog-boot.runtime.js';
import { applyInterfacePreferences, renderSectionTabs, renderView, syncKuniQuick } from '../shell/navigation.runtime.js';

export var canCreateUniversity = () => isProjectAdmin();

export var canCreateInstitute = () => isProjectAdmin() || adminHasRole('university_admin');

export var canCreateProgram = () => isProjectAdmin() || adminHasRole('university_admin') || adminHasRole('institute_admin');

export var canCreateGroup = canCreateProgram;

function exactScopedRole(role, scopeType, scopeID) {
  return state.roles.some((item)=>item.role===role && item.scope?.type===scopeType && item.scope?.id===scopeID);
}

export function canEditOrgEntity(type,item,org={universities:[],institutes:[],programs:[]}) {
  if (!item) return false;
  if (isProjectAdmin()) return true;
  if (type === 'university') return exactScopedRole('university_admin','university',item.id);
  if (type === 'institute') return exactScopedRole('university_admin','university',item.university_id);
  if (type === 'program') {
    const institute=(org.institutes||[]).find((v)=>v.id===item.institute_id);
    return exactScopedRole('institute_admin','institute',item.institute_id) || (!!institute && exactScopedRole('university_admin','university',institute.university_id));
  }
  if (type === 'group') {
    return exactScopedRole('group_admin','group',item.id) || exactScopedRole('institute_admin','institute',item.institute_id) || exactScopedRole('university_admin','university',item.university_id);
  }
  return false;
}

export function adminCatalogTypes() {
  if (isProjectAdmin() || adminHasRole('university_admin')) return ['subject','teacher','room'];
  if (adminHasRole('institute_admin')) return ['subject','teacher'];
  return [];
}

export function canManageCatalogItem(type,item,org={universities:[],institutes:[],programs:[]}) {
  if (isProjectAdmin()) return true;
  if (type === 'room') return !!item?.university_id && exactScopedRole('university_admin','university',item.university_id);
  if (type === 'teacher') {
    if (!item?.institute_id) return false;
    const institute=(org.institutes||[]).find((v)=>v.id===item.institute_id);
    return exactScopedRole('institute_admin','institute',item.institute_id) || (!!institute && exactScopedRole('university_admin','university',institute.university_id));
  }
  if (type === 'subject') {
    if (!item?.academic_program_id) return false;
    const program=(org.programs||[]).find((v)=>v.id===item.academic_program_id);
    if (!program) return false;
    const institute=(org.institutes||[]).find((v)=>v.id===program.institute_id);
    return exactScopedRole('institute_admin','institute',program.institute_id) || (!!institute && exactScopedRole('university_admin','university',institute.university_id));
  }
  return false;
}

export var canManageGroup = () => hasRole('group_admin') || hasRole('group_editor') || hasScopedRole('institute_admin','group',state.groupID) || hasScopedRole('university_admin','group',state.groupID) || isProjectAdmin();

export var canHeadGroup = () => hasRole('group_admin') || hasRole('group_head') || hasScopedRole('institute_admin','group',state.groupID) || hasScopedRole('university_admin','group',state.groupID) || isProjectAdmin();

export var canManageWiki = () => { const id=currentGroup()?.university_id; return !!id && (isProjectAdmin() || hasScopedRole('university_admin','university',id) || hasScopedRole('wiki_editor','university',id) || hasRole('content_moderator') || hasRole('student_admin')); };

export var canManageNavigator = () => { const id=currentGroup()?.university_id; return !!id && (isProjectAdmin() || hasScopedRole('university_admin','university',id) || hasScopedRole('campus_editor','university',id)); };

export var canOpenAnyCampusEditor = () => !state.isGuest && (isProjectAdmin() || state.roles.some((item)=>['university_admin','campus_editor'].includes(item.role) && item.scope?.type==='university'));

export var canManageEvents = () => { const id=currentGroup()?.university_id; return !!id && (isProjectAdmin() || hasScopedRole('university_admin','university',id) || hasScopedRole('event_editor','university',id)); };

export var canManageStudentCouncil = () => { const id=currentGroup()?.university_id; return !!id && (isProjectAdmin() || hasScopedRole('university_admin','university',id) || hasScopedRole('student_council_member','university',id)); };

export var canManageNotes = () => canManageGroup();

export var canUploadNotes = () => canManageGroup() || !!currentMembership();

export function visibilityScopeFieldHTML(value='group') {
  return `<label>Видимость<select name="visibility_scope"><option value="group" ${value!=='group_friends'?'selected':''}>Только моя группа</option><option value="group_friends" ${value==='group_friends'?'selected':''}>Группа + друзья</option></select><small class="optional">После принятия двусторонней дружбы друзья увидят запись в своём потоке; конспект или решение привяжется к ближайшей подходящей паре их расписания.</small></label>`;
}

function visibilityScopeLabel(value){ return value==='group_friends'?'Группа + друзья':'Только группа'; }

export var currentMembership = () => state.memberships.find((m) => m.group_id === state.groupID && m.status === 'verified');

export function adminAccessibleGroups() {
  if (isProjectAdmin()) return state.groups;
  return state.groups.filter((group) => state.roles.some((item) => {
    if (item.role === 'university_admin') return item.scope.type === 'university' && item.scope.id === group.university_id;
    if (item.role === 'institute_admin') return item.scope.type === 'institute' && item.scope.id === group.institute_id;
    if (item.role === 'group_admin') return item.scope.type === 'group' && item.scope.id === group.id;
    if (['wiki_editor','campus_editor','event_editor','student_council_member'].includes(item.role)) return item.scope.type === 'university' && item.scope.id === group.university_id;
    return false;
  }));
}

export function setBusy(form, busy) {
  $$('button,input,select,textarea', form).forEach((el) => { el.disabled = busy; });
}

export function setAuthStage(stage) {
  const box = $('.auth-box');
  if (!box) return;
  const next = ['welcome', 'invite', 'login', 'register', 'guest'].includes(stage) ? stage : 'welcome';
  box.dataset.authStage = next;
  document.body.dataset.authStage = next;
  // User navigated to real auth CTAs — only then drop the boot overlay.
  if ((next === 'login' || next === 'register' || next === 'welcome' || next === 'guest') && isAppBootActive()) {
    const auth = $('#auth-view');
    document.documentElement.classList.add('kuni-auth-ready');
    if (auth) auth.hidden = false;
    clearAppBoot();
  }
  $$('.auth-stage', box).forEach((panel) => {
    panel.hidden = panel.dataset.stage !== next;
  });
  if (next === 'login') {
    $('#login-tab')?.classList.add('active');
    $('#register-tab')?.classList.remove('active');
  }
  if (next === 'register') {
    $('#register-tab')?.classList.add('active');
    $('#login-tab')?.classList.remove('active');
  }
  setAuthError();
  syncInviteStageCopy();
  // Don't focus anything under the boot overlay (auth is not on screen).
  if (isAppBootActive()) return;
  const focusTarget = next === 'login' ? $('#login-form input[name="email"]')
    : next === 'register' ? $('#register-form input[name="display_name"]')
    : next === 'guest' ? $('#auth-group-search-input')
    : next === 'invite' ? $('#invite-go-register')
    : $('#auth-go-register');
  setTimeout(() => focusTarget?.focus?.(), 40);
}

function switchAuth(register) {
  setAuthStage(register ? 'register' : 'login');
  $('#login-tab')?.classList.toggle('active', !register);
  $('#register-tab')?.classList.toggle('active', register);
  setAuthError();
}

function telegramReturnTo() {
  const url = new URL(window.location.href);
  url.searchParams.delete('auth_error');
  return `${url.pathname}${url.search}${url.hash}` || '/';
}

export async function initTelegramLogin() {
  const nodes = [
    ['#telegram-login', '#telegram-auth-divider'],
    ['#invite-telegram-login', '#invite-telegram-divider'],
    ['#register-telegram-login', '#register-telegram-divider'],
  ];
  let enabled = false;
  try {
    const response = await rawRequest('/auth/telegram/status');
    const body = await parseBody(response);
    enabled = response.ok && body?.data?.enabled === true;
  } catch {
    enabled = false;
  }
  nodes.forEach(([btnSel, divSel]) => {
    const button = $(btnSel);
    const divider = $(divSel);
    if (button) button.hidden = !enabled;
    if (divider) divider.hidden = !enabled;
  });
}

function startTelegramAuth() {
  setAuthError();
  // Preserve /join/<code> path so invite is applied after Telegram OIDC return.
  const target = `${API}/auth/telegram/start?return_to=${encodeURIComponent(telegramReturnTo())}`;
  window.location.assign(target);
}

export function consumeTelegramAuthError() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('auth_error');
  if (!code) return;
  const messages = {
    telegram_cancelled: 'Вход через Telegram отменён.',
    telegram_state_missing: 'Сессия входа через Telegram истекла. Попробуйте ещё раз.',
    telegram_state_invalid: 'Не удалось проверить вход через Telegram. Попробуйте ещё раз.',
    telegram_login_failed: 'Не удалось войти через Telegram. Попробуйте ещё раз.',
  };
  setAuthError(messages[code] || 'Не удалось войти через Telegram.');
  url.searchParams.delete('auth_error');
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

async function authenticate(path, form) {
  setAuthError();
  const data = Object.fromEntries(new FormData(form));
  if (!data.username) delete data.username;
  if (path.endsWith('/register')) {
    const docs = await loadLegalDocuments();
    const checked = !!$('#registration-pd-consent')?.checked;
    data.personal_data_consent = checked;
    data.consent_version = docs?.consent?.version || '';
    data.privacy_version = docs?.privacy?.version || '';
    if (!checked) {
      setAuthError('Для регистрации необходимо дать отдельное согласие на обработку персональных данных.');
      return;
    }
  }
  setBusy(form, true);
  try {
    const result = await api(path, { method: 'POST', body: JSON.stringify(data) }, false);
    state.accessToken = result.access_token;
    state.user = result.user;
    syncAndroidAccessToken();
    await enterApp();
    trackProduct(path.endsWith('/register') ? 'signup.completed' : 'login.completed');
  } catch (error) {
    setAuthError(`${error.message}${error.requestID ? ` · ${error.requestID}` : ''}`);
  } finally { setBusy(form, false); }
}

export async function handlePasswordResetLink(token) {
  openDialog('Новый пароль', `<form class="form" id="reset-password-form"><p class="muted">После смены пароля все активные сеансы будут завершены.</p><label>Новый пароль<input name="password" type="password" autocomplete="new-password" minlength="10" maxlength="256" required></label><label>Повторите пароль<input name="confirm" type="password" autocomplete="new-password" minlength="10" maxlength="256" required></label></form>`, [
    {label:'Сбросить пароль',primary:true,action:async()=>{const form=$('#reset-password-form');if(!form.reportValidity())return;if(form.password.value!==form.confirm.value){toast('Пароли не совпадают');return;}await api('/auth/password/reset',{method:'POST',body:JSON.stringify({token,password:form.password.value})},false);closeDialog();state.accessToken='';state.user=null;replaceLocationPath('/');switchAuth(false);setAuthError('Пароль изменён. Войдите заново.');}},
  ]);
}

function lockSecurityDialog(locked) {
  const dialog=$('#dialog'), close=$('#dialog-close');
  if(close) close.hidden=locked;
  dialog.dataset.securityLocked=locked?'true':'';
}

function recoveryCodesHTML(codes) { return `<div class="recovery-codes">${codes.map((code)=>`<code>${escapeHTML(code)}</code>`).join('')}</div>`; }

function showRecoveryCodes(result,{required=false,onDone=()=>{}}={}) {
  let finished=false;
  const finish=()=>{
    if(finished)return;
    finished=true;
    if(required)lockSecurityDialog(false);
    closeDialog();
    onDone();
  };
  const body=`<div class="form mfa-recovery-screen"><p><strong>Сохраните эти коды сейчас.</strong> Каждый работает один раз и больше не будет показан.</p>${recoveryCodesHTML(result?.recovery_codes||[])}<div class="mfa-recovery-inline-actions"><button class="button primary" id="mfa-recovery-continue" type="button">Продолжить</button></div></div>`;
  openDialog('Коды восстановления',body,[{label:'Продолжить',primary:true,action:finish}]);
  $('#mfa-recovery-continue')?.addEventListener('click',(event)=>{event.preventDefault();finish();});
  if(required)lockSecurityDialog(true);
}

async function setupMFARequired() {
  const setup=await api('/auth/mfa/setup/start',{method:'POST',body:'{}'});
  return new Promise((resolve,reject)=>{
    openDialog('Защитите привилегированный аккаунт', `<div class="form mfa-setup"><p>Для вашей роли двухфакторная аутентификация обязательна. Отсканируйте QR в Aegis, 2FAS, Google Authenticator, Microsoft Authenticator, 1Password или другом TOTP-клиенте.</p><div class="qr-panel"><div class="qr-code" id="mfa-qr" aria-label="QR-код для настройки двухфакторной аутентификации"></div><div class="qr-copy"><strong>Не получается отсканировать?</strong><span>Секрет можно скопировать и добавить в приложение вручную.</span><div class="mfa-secret">${escapeHTML(setup.secret)}</div><div class="inline qr-actions"><button class="button small" id="mfa-copy-secret" type="button">Скопировать секрет</button><button class="button small" id="mfa-copy-uri" type="button">Скопировать setup-ссылку</button><button class="button small" id="mfa-download-qr" type="button">Скачать QR</button></div></div></div><label>Код из приложения<input id="mfa-setup-code" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="123456"></label></div>`, [
      {label:'Подтвердить',primary:true,action:async()=>{const code=$('#mfa-setup-code').value.trim();try{const result=await api('/auth/mfa/setup/confirm',{method:'POST',body:JSON.stringify({code})});trackProduct('mfa.enabled');showRecoveryCodes(result,{required:true,onDone:resolve});}catch(error){toast(error.message);}}},
    ]);
    renderQRCode('#mfa-qr', setup.otpauth_uri || setup.uri, {size:220});
    $('#mfa-copy-secret')?.addEventListener('click',()=>copyText(setup.secret,'Секрет скопирован').catch(error=>toast(error.message)));
    $('#mfa-copy-uri')?.addEventListener('click',()=>copyText(setup.otpauth_uri || setup.uri,'Setup-ссылка скопирована').catch(error=>toast(error.message)));
    $('#mfa-download-qr')?.addEventListener('click',()=>{try{downloadQRCode('#mfa-qr','kuni-mfa.png');}catch(error){toast(error.message);}});
    lockSecurityDialog(true);
  });
}

export async function setupMFAOptional() {
  const setup=await api('/auth/mfa/setup/start',{method:'POST',body:'{}'});
  return new Promise((resolve)=>{
    openDialog('Включить двухфакторную аутентификацию', `<div class="form mfa-setup"><p>После включения при каждом новом входе kUni будет запрашивать код из приложения-аутентификатора. Отсканируйте QR или скопируйте секрет/setup-ссылку вручную.</p><div class="qr-panel"><div class="qr-code" id="mfa-qr" aria-label="QR-код для настройки двухфакторной аутентификации"></div><div class="qr-copy"><strong>Ручная настройка</strong><span>Секрет и setup-ссылка остаются доступны как fallback.</span><div class="mfa-secret">${escapeHTML(setup.secret)}</div><div class="inline qr-actions"><button class="button small" id="mfa-copy-secret" type="button">Скопировать секрет</button><button class="button small" id="mfa-copy-uri" type="button">Скопировать setup-ссылку</button><button class="button small" id="mfa-download-qr" type="button">Скачать QR</button></div></div></div><label>Код из приложения<input id="mfa-setup-code" inputmode="numeric" autocomplete="one-time-code" maxlength="8" placeholder="123456"></label></div>`, [
      {label:'Отмена',action:()=>{closeDialog();resolve(false);}},
      {label:'Включить 2FA',primary:true,action:async()=>{const code=$('#mfa-setup-code').value.trim();if(!code){toast('Введите код из приложения');return;}try{const result=await api('/auth/mfa/setup/confirm',{method:'POST',body:JSON.stringify({code})});trackProduct('mfa.enabled');showRecoveryCodes(result,{onDone:()=>resolve(true)});}catch(error){toast(error.message);}}},
    ]);
    renderQRCode('#mfa-qr', setup.otpauth_uri || setup.uri, {size:220});
    $('#mfa-copy-secret')?.addEventListener('click',()=>copyText(setup.secret,'Секрет скопирован').catch(error=>toast(error.message)));
    $('#mfa-copy-uri')?.addEventListener('click',()=>copyText(setup.otpauth_uri || setup.uri,'Setup-ссылка скопирована').catch(error=>toast(error.message)));
    $('#mfa-download-qr')?.addEventListener('click',()=>{try{downloadQRCode('#mfa-qr','kuni-mfa.png');}catch(error){toast(error.message);}});
  });
}

async function verifyMFARequired() {
  return new Promise((resolve)=>{
    openDialog('Двухфакторная аутентификация', `<div class="form"><p>Введите 6-значный TOTP-код или один recovery code.</p><label>Код<input id="mfa-session-code" autocomplete="one-time-code" placeholder="123456 или recovery code"></label></div>`, [
      {label:'Продолжить',primary:true,action:async()=>{try{await api('/auth/mfa/verify-session',{method:'POST',body:JSON.stringify({code:$('#mfa-session-code').value.trim()})});lockSecurityDialog(false);closeDialog();resolve();}catch(error){toast(error.message);}}},
    ]);
    lockSecurityDialog(true);
  });
}

export async function ensureMFAForSession() {
  const status=await api('/auth/mfa/status');
  if(status?.privileged && !status.enabled){await setupMFARequired();return api('/auth/mfa/status');}
  if(status?.enabled && !status.session_verified){await verifyMFARequired();return api('/auth/mfa/status');}
  return status;
}

export async function ensurePersonalDataConsent() {
  const status = await api('/legal/status');
  state.legalStatus = status;
  if (status?.accepted) return status;
  const docs = await loadLegalDocuments();
  const consent = docs?.consent;
  if (!consent || consent.version !== status?.consent_version || consent.privacy_version !== status?.privacy_version) {
    throw new Error('Версии юридических документов на клиенте и сервере не совпадают. Обновите страницу.');
  }
  return new Promise((resolve) => {
    const body = `<div class="legal-consent-gate"><span class="eyebrow">${escapeHTML(t('legal.gate.kicker'))}</span><p>${escapeHTML(t('legal.gate.body'))}</p><div class="legal-gate-links"><a href="/privacy" target="_blank" rel="noopener">${escapeHTML(t('legal.links.privacy'))}</a><a href="/consent" target="_blank" rel="noopener">${escapeHTML(t('legal.links.consent'))}</a></div><label class="legal-consent-check legal-gate-check"><input id="legal-gate-consent" type="checkbox"><span>${escapeHTML(t('legal.gate.checkbox'))}</span></label><small>${escapeHTML(t('legal.page.version',{version:status.consent_version,date:consent.effective_at||'—'}))}</small></div>`;
    openDialog(t('legal.gate.title'), body, [
      {label:t('legal.gate.logout'),action:async()=>{
        try { const csrf=decodeURIComponent(cookie('kuni_csrf')||''); await rawRequest('/auth/logout',{method:'POST',headers:{'X-CSRF-Token':csrf},body:'{}'}); } catch {}
        lockSecurityDialog(false); closeDialog(); leaveApp(); resolve(null);
      }},
      {label:t('legal.gate.accept'),primary:true,disabled:true,action:async()=>{
        const check=$('#legal-gate-consent');
        if(!check?.checked){toast('Поставьте отметку согласия');return;}
        const accepted=await api('/legal/consent',{method:'POST',body:JSON.stringify({accepted:true,consent_version:status.consent_version,privacy_version:status.privacy_version})});
        state.legalStatus=accepted;
        lockSecurityDialog(false); closeDialog(); toast(t('legal.gate.accepted')); resolve(accepted);
      }},
    ]);
    lockSecurityDialog(true);
    const check=$('#legal-gate-consent');
    const acceptButton=$('#dialog-footer .button.primary');
    check?.addEventListener('change',()=>{if(acceptButton)acceptButton.disabled=!check.checked;});
  });
}

export function renderAccount() {
  $('#account-name').textContent = state.isGuest ? 'Гостевой просмотр' : (state.user?.display_name || 'Студент');
  $('#account-email').textContent = state.isGuest ? 'Только чтение' : (state.user?.email || '');
  $('#avatar').textContent = state.isGuest ? 'Г' : (state.user?.display_name?.trim().charAt(0).toUpperCase() || 'k');
}

export async function loadBase() {
  const [memberships, groups, roles, remotePreferences] = await Promise.all([
    api('/memberships/me'), loadAllGroupPages('/groups'), api('/roles/me'),
    api('/interface-preferences').catch(() => null),
  ]);
  state.memberships = memberships || [];
  state.groups = groups?.items || groups || [];
  state.roles = roles || [];
  applyInterfacePreferences(remotePreferences || loadLocalInterfacePreferences());
  saveLocalInterfacePreferences(state.interfacePreferences);
  const verified = state.memberships.filter((m) => m.status === 'verified');
  const saved = localStorage.getItem('kuni-group-id');
  const managedGroups = adminAccessibleGroups();
  const allowedIDs = [...new Set([...verified.map((m) => m.group_id), ...managedGroups.map((g) => g.id)])];
  state.groupID = allowedIDs.includes(saved) ? saved : (verified[0]?.group_id || managedGroups[0]?.id || '');
  renderGroupSelector();
}

function groupOptionLabel(group,fallback='') {
  if (!group) return fallback || 'Группа';
  return [group.university_name, group.institute_name, group.name||fallback].filter(Boolean).join(' · ');
}

export function groupChoiceFromGroup(group,fallback='',id='') {
  const source=group||{};
  const groupID=String(source.id||id||'');
  const shortName=String(source.name||fallback||'Группа');
  return {
    ...source,
    id:groupID,
    group_name:shortName,
    name:groupOptionLabel(source,shortName),
    university_name:String(source.university_name||''),
    institute_name:String(source.institute_name||''),
    program_name:String(source.program_name||source.academic_program_name||''),
    program_code:String(source.program_code||''),
    current_course:Number(source.current_course||0),
  };
}

export function availableGroupChoices() {
  const verified = (state.memberships || []).filter((m) => m.status === 'verified');
  const byID = new Map((state.groups || []).map((group) => [String(group.id), group]));
  if (state.isGuest) {
    return (state.groups || []).filter((g) => g.status !== 'archived').map((g) => groupChoiceFromGroup(g));
  }
  const managed = adminAccessibleGroups().filter((g) => g.status !== 'archived').map((g) => groupChoiceFromGroup(g));
  const own = verified.map((m) => groupChoiceFromGroup(byID.get(String(m.group_id)), m.group_name, m.group_id));
  return [...new Map([...own, ...managed].map((x) => [String(x.id), x])).values()];
}

function currentGroupChoice(){const found=availableGroupChoices().find((g)=>String(g.id)===String(state.groupID));if(found)return found;const group=currentGroup();return group?groupChoiceFromGroup(group):null;}

export function currentGroupLabel() {
  return currentGroupChoice()?.name || 'Группа не выбрана';
}

function currentGroupShortLabel(){return currentGroupChoice()?.group_name||'Группа не выбрана';}

function currentGroupContextLabel(){
  const group=currentGroupChoice();
  if(!group)return '';
  const program=[group.program_code,group.program_name].filter(Boolean).join(' · ');
  return [group.university_name,group.institute_name,program,group.current_course?`${group.current_course} курс`:''].filter(Boolean).join(' · ');
}

export function renderGroupSelector() {
  const items = availableGroupChoices();
  const active=items.find((g)=>String(g.id)===String(state.groupID))||null;
  const short=active?.group_name||currentGroup()?.name||'Группа не выбрана';
  const context=active?currentGroupContextLabel():'';
  ['#group-selector','#guest-group-selector'].forEach((selector) => {
    const control = $(selector);
    if (!control) return;
    if (control.tagName === 'SELECT') {
      const optionsHTML = items.length ? items.map((m) => `<option value="${m.id}">${escapeHTML(m.name)}</option>`).join('') : `<option value="">${escapeHTML(t('shell.group.none'))}</option>`;
      control.innerHTML=optionsHTML;control.value=state.groupID;
      return;
    }
    const shortNode=control.querySelector('[data-current-group-short]');
    if(shortNode)shortNode.textContent=short;
    const contextNode=control.querySelector('[data-current-group-context]');
    if(contextNode){contextNode.textContent=context;contextNode.hidden=!context;}
    control.title=currentGroupLabel();
    control.disabled=!items.length;
  });
  $$('[data-current-group-label]').forEach((node) => { node.textContent = currentGroupLabel(); });
}

function groupPickerNormalize(value){return String(value||'').toLocaleLowerCase('ru').replaceAll('ё','е').replace(/\s+/g,' ').trim();}

function groupPickerHaystack(group){return groupPickerNormalize([group.group_name,group.university_name,group.institute_name,group.program_code,group.program_name,group.current_course?`${group.current_course} курс`:'',group.admission_year].filter(Boolean).join(' '));}

function groupPickerMatches(group,query){const terms=groupPickerNormalize(query).split(' ').filter(Boolean);const hay=groupPickerHaystack(group);return terms.every((term)=>hay.includes(term));}

function groupPickerSort(items){return [...items].sort((a,b)=>[a.university_name,a.institute_name,a.program_code||a.program_name,a.current_course,a.group_name].join(' ').localeCompare([b.university_name,b.institute_name,b.program_code||b.program_name,b.current_course,b.group_name].join(' '),'ru',{numeric:true}));}

function groupPickerItemHTML(group,selectedID=state.groupID){
  const meta=[[group.program_code,group.program_name].filter(Boolean).join(' · '),group.current_course?`${group.current_course} курс`:''].filter(Boolean).join(' · ');
  const active=String(group.id)===String(selectedID);
  return `<button type="button" class="group-picker-item ${active?'active':''}" data-group-picker-id="${escapeHTML(group.id)}"><span><strong>${escapeHTML(group.group_name||group.name||'Группа')}</strong><small>${escapeHTML(meta||group.institute_name||group.university_name||'Учебная группа')}</small></span>${active?'<b>✓</b>':'<i>→</i>'}</button>`;
}

function groupPickerPlural(n,one,few,many){const value=Math.abs(Number(n)||0)%100;const tail=value%10;if(value>10&&value<20)return many;if(tail===1)return one;if(tail>=2&&tail<=4)return few;return many;}

function groupPickerBucket(items,keyFn,labelFn){
  const map=new Map();
  items.forEach((item)=>{const key=String(keyFn(item)||'other');if(!map.has(key))map.set(key,{label:labelFn(item)||'Другое',items:[]});map.get(key).items.push(item);});
  return [...map.entries()].map(([key,value])=>({key,...value})).sort((a,b)=>a.label.localeCompare(b.label,'ru',{numeric:true}));
}

function groupPickerHierarchyHTML(items,selectedID=state.groupID){
  const universities=groupPickerBucket(groupPickerSort(items),g=>g.university_id||g.university_name,g=>g.university_name||'Университет');
  return `<div class="group-picker-tree">${universities.map((university)=>{
    const uOpen=university.items.some(g=>String(g.id)===String(selectedID));
    const institutes=groupPickerBucket(university.items,g=>g.institute_id||g.institute_name,g=>g.institute_name||'Институт / факультет');
    return `<details class="group-picker-node group-picker-university" ${uOpen?'open':''}><summary><span><strong>${escapeHTML(university.label)}</strong><small>${university.items.length} ${groupPickerPlural(university.items.length,'группа','группы','групп')}</small></span><b>⌄</b></summary><div class="group-picker-children">${institutes.map((institute)=>{
      const iOpen=institute.items.some(g=>String(g.id)===String(selectedID));
      const programs=groupPickerBucket(institute.items,g=>g.academic_program_id||`${g.program_code}:${g.program_name}`,g=>[g.program_code,g.program_name].filter(Boolean).join(' · ')||'Без программы');
      return `<details class="group-picker-node group-picker-institute" ${iOpen?'open':''}><summary><span><strong>${escapeHTML(institute.label)}</strong><small>${institute.items.length} ${groupPickerPlural(institute.items.length,'группа','группы','групп')}</small></span><b>⌄</b></summary><div class="group-picker-children">${programs.map((program)=>{
        const pOpen=program.items.some(g=>String(g.id)===String(selectedID));
        const courses=groupPickerBucket(program.items,g=>g.current_course||0,g=>g.current_course?`${g.current_course} курс`:'Курс не указан');
        return `<details class="group-picker-node group-picker-program" ${pOpen?'open':''}><summary><span><strong>${escapeHTML(program.label)}</strong><small>${program.items.length} ${groupPickerPlural(program.items.length,'группа','группы','групп')}</small></span><b>⌄</b></summary><div class="group-picker-children">${courses.map((course)=>`<section class="group-picker-course"><div class="group-picker-course-title"><span>${escapeHTML(course.label)}</span><small>${course.items.length}</small></div><div class="group-picker-list">${groupPickerSort(course.items).map(g=>groupPickerItemHTML(g,selectedID)).join('')}</div></section>`).join('')}</div></details>`;
      }).join('')}</div></details>`;
    }).join('')}</div></details>`;
  }).join('')}</div>`;
}

function groupPickerRecentIDs(){try{const value=JSON.parse(localStorage.getItem(state.isGuest?'kuni-guest-recent-groups':'kuni-recent-groups')||'[]');return Array.isArray(value)?value.map(String).slice(0,6):[];}catch{return [];}}

function rememberGroupChoice(id){try{const key=state.isGuest?'kuni-guest-recent-groups':'kuni-recent-groups';const next=[String(id),...groupPickerRecentIDs().filter(v=>String(v)!==String(id))].slice(0,6);localStorage.setItem(key,JSON.stringify(next));}catch{/* ignore */}}

export function openGroupPickerMenu({items=availableGroupChoices(),selectedID=state.groupID,title='Выберите группу',onSelect=null}={}){
  const unique=[...new Map((items||[]).filter(Boolean).map((group)=>{const choice=group.group_name?group:groupChoiceFromGroup(group);return [String(choice.id),choice];})).values()];
  if(!unique.length){toast('Нет доступных групп','warn');return;}
  const choose=onSelect||((group)=>selectGroup(group.id));
  if(unique.length<=5){
    openDialog(title,`<div class="group-picker-shell compact"><div class="group-picker-list">${groupPickerSort(unique).map(g=>groupPickerItemHTML(g,selectedID)).join('')}</div></div>`,[{label:'Закрыть',action:closeDialog}]);
  }else{
    const byID=new Map(unique.map(g=>[String(g.id),g]));
    const recent=groupPickerRecentIDs().map(id=>byID.get(String(id))).filter(Boolean);
    const current=byID.get(String(selectedID));
    const quick=[...new Map([current,...recent].filter(Boolean).map(g=>[String(g.id),g])).values()].slice(0,5);
    openDialog(title,`<div class="group-picker-shell" id="group-picker-shell"><div class="group-picker-search"><span aria-hidden="true">⌕</span><input id="group-picker-search" type="search" autocomplete="off" placeholder="Группа, ВУЗ, институт, программа…" aria-label="Поиск группы"><kbd>${unique.length}</kbd></div>${quick.length?`<section class="group-picker-quick"><div class="group-picker-section-title"><strong>Быстрый доступ</strong><small>${unique.length} всего</small></div><div class="group-picker-quick-list">${quick.map(g=>groupPickerItemHTML(g,selectedID)).join('')}</div></section>`:''}<section class="group-picker-catalog"><div class="group-picker-section-title"><strong>По структуре</strong><small>ВУЗ → институт → программа → курс</small></div><div id="group-picker-tree">${groupPickerHierarchyHTML(unique,selectedID)}</div><div id="group-picker-results" hidden></div></section></div>`,[{label:'Закрыть',action:closeDialog}]);
    const input=$('#group-picker-search');const tree=$('#group-picker-tree');const results=$('#group-picker-results');
    const rerenderSearch=()=>{const query=input?.value||'';if(!query.trim()){tree.hidden=false;results.hidden=true;results.innerHTML='';return;}const found=groupPickerSort(unique.filter(g=>groupPickerMatches(g,query)));tree.hidden=true;results.hidden=false;results.innerHTML=found.length?`<div class="group-picker-search-count">Найдено: ${found.length}</div><div class="group-picker-list">${found.slice(0,120).map(g=>groupPickerItemHTML(g,selectedID)).join('')}</div>${found.length>120?'<div class="group-picker-search-count">Уточните запрос, чтобы сузить результаты.</div>':''}`:'<div class="group-picker-empty"><strong>Ничего не найдено</strong><span>Ищи по группе, ВУЗу, институту, программе или курсу.</span></div>';bind();};
    const bind=()=>{$$('[data-group-picker-id]',$('#dialog-body')).forEach(button=>{if(button.dataset.bound==='1')return;button.dataset.bound='1';button.addEventListener('click',()=>{const group=byID.get(String(button.dataset.groupPickerId));if(!group)return;rememberGroupChoice(group.id);transitionDialog(()=>Promise.resolve(choose(group)).catch(error=>toast(error.message)));});});};
    input?.addEventListener('input',rerenderSearch);bind();setTimeout(()=>input?.focus({preventScroll:true}),40);return;
  }
  const byID=new Map(unique.map(g=>[String(g.id),g]));
  $$('[data-group-picker-id]',$('#dialog-body')).forEach(button=>button.addEventListener('click',()=>{const group=byID.get(String(button.dataset.groupPickerId));if(!group)return;rememberGroupChoice(group.id);transitionDialog(()=>Promise.resolve(choose(group)).catch(error=>toast(error.message)));}));
}

export async function selectGroup(id) {
  if (!id || String(id) === String(state.groupID)) return false;
  state.groupID = id;
  state.courseSubjectID = '';
  state.contextEntryID = '';
  if (state.view === 'lesson') {
    state.view = 'schedule';
    document.body.dataset.view = 'schedule';
    if (typeof renderSectionTabs === 'function') renderSectionTabs();
  }
  try { localStorage.setItem(state.isGuest ? 'kuni-guest-group-id' : 'kuni-group-id', state.groupID); } catch { /* ignore */ }
  rememberGroupChoice(id);
  renderGroupSelector();
  await renderView();
  return true;
}

export function openGroupSwitcher() {
  const items = availableGroupChoices();
  openGroupPickerMenu({items,selectedID:state.groupID,title:items.length>5?'Выбор группы':'Группа',onSelect:async(group)=>{if(await selectGroup(group.id))toast(`Группа: ${group.group_name||group.name||''}`,'ok');}});
}

export function updateGuestChrome() {
  document.body.classList.toggle('guest-mode', state.isGuest);
  const allowedSections = new Set(['today','campus']);
  const guestLabels = { today:t('section.schedule'), campus:t('section.navigator') };
  $$('.section-nav-item').forEach((button) => {
    button.hidden = state.isGuest && !allowedSections.has(button.dataset.section);
    const label = $('span', button);
    if (label) label.textContent = state.isGuest ? (guestLabels[button.dataset.section] || t(sectionDefs[button.dataset.section]?.labelKey || '')) : (t(sectionDefs[button.dataset.section]?.labelKey || '') || label.textContent);
  });
  $$('[data-mobile-section]').forEach((button) => {
    button.hidden = state.isGuest && !allowedSections.has(button.dataset.mobileSection);
    const label = $('span', button);
    if (label) label.textContent = state.isGuest ? (guestLabels[button.dataset.mobileSection] || t(sectionDefs[button.dataset.mobileSection]?.labelKey || '')) : (t(sectionDefs[button.dataset.mobileSection]?.labelKey || '') || label.textContent);
  });
  $('.persistent-actions').hidden = false;
  $('#telegram-shortcut').hidden = state.isGuest;
  $('#help-onboarding-button').hidden = state.isGuest;
  $('#personalization-button').hidden = state.isGuest;
  $('#guest-exit-shortcut').hidden = !state.isGuest;
  const mobileGroup = $('#guest-mobile-group');
  if (mobileGroup) {
    const hideOnSuperGrok = typeof superGrokMode === 'function' && superGrokMode();
    mobileGroup.hidden = !!state.publicTeacher || hideOnSuperGrok;
  }
  const groupWrap=$('.group-selector-wrap'); if(groupWrap) groupWrap.hidden=!!state.publicTeacher;
  if(!state.publicTeacher) $('.group-selector-wrap label').textContent = state.isGuest ? t('shell.group.view') : t('shell.group.current');
  $('#logout').title = state.isGuest ? t('shell.guest.logout') : t('shell.logout');
  $('#logout').setAttribute('aria-label', $('#logout').title);
  $('#connection-state').textContent = state.isGuest ? t('shell.guest.mode') : t('shell.sync.ok');
  syncGuestInviteBar();
  if (typeof syncKuniQuick === 'function') syncKuniQuick();
}

function syncGuestInviteBar() {
  const bar = $('#guest-invite-bar');
  if (!bar) return;
  const show = Boolean(state.isGuest && state.pendingInviteCode && state.invitePreview?.group);
  bar.hidden = !show;
  if (!show) return;
  const group = state.invitePreview.group;
  const title = $('#guest-invite-bar-title');
  const text = $('#guest-invite-bar-text');
  if (title) title.textContent = `Группа «${group.name}» · гость`;
  if (text) {
    text.textContent = state.invitePreview.approval_required
      ? 'Создайте аккаунт, чтобы отправить заявку на вступление.'
      : 'Создайте аккаунт, чтобы вступить и открыть задания, автомат и материалы.';
  }
}

export function leaveGuestForInviteAuth(stage = 'register') {
  leaveApp();
  setAuthStage(stage);
}

function onboardingSnoozed() {
  try {
    const until = Number(localStorage.getItem('kuni-onboarding-v14-snooze') || 0);
    return until > Date.now();
  } catch { return false; }
}

export function snoozeOnboarding(hours = 24) {
  try { localStorage.setItem('kuni-onboarding-v14-snooze', String(Date.now() + hours * 3600 * 1000)); }
  catch { /* ignore */ }
}

export async function completeOnboardingV13() {
  await api('/product-state/onboarding/complete', { method: 'POST', body: JSON.stringify({ version: ONBOARDING_VERSION }) }).catch(() => {});
  try { localStorage.removeItem('kuni-onboarding-v14-snooze'); } catch { /* ignore */ }
}

async function maybeOpenV12Onboarding() {
  return maybeOpenProductOnboarding();
}

export async function maybeOpenProductOnboarding({ force = false, reason = '' } = {}) {
  if (state.isGuest || $('#dialog')?.open) return;
  if (!force && onboardingSnoozed()) return;
  if (!force) {
    const product = await api('/product-state').catch(() => null);
    if (!product) return;
    if (Number(product.onboarding_version || 0) >= ONBOARDING_VERSION) return;
  }
  openOnboardingWizardV13({ force, reason });
}

export function initSessionRuntime() {
  $$('[data-password-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = document.getElementById(button.dataset.passwordToggle);
      if (!input) return;
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      button.setAttribute('aria-pressed', show ? 'true' : 'false');
      button.setAttribute('aria-label', show ? 'Скрыть пароль' : 'Показать пароль');
      button.title = show ? 'Скрыть пароль' : 'Показать пароль';
      const eye = button.querySelector('.auth-password-icon-show');
      const hide = button.querySelector('.auth-password-icon-hide');
      if (eye) eye.hidden = show;
      if (hide) hide.hidden = !show;
    });
  });

  $('#login-tab')?.addEventListener('click', () => switchAuth(false));

  $('#register-tab')?.addEventListener('click', () => switchAuth(true));

  $('#auth-go-register')?.addEventListener('click', () => setAuthStage('register'));

  $('#auth-go-login')?.addEventListener('click', () => setAuthStage('login'));

  $('#invite-go-register')?.addEventListener('click', () => setAuthStage('register'));

  $('#invite-go-login')?.addEventListener('click', () => setAuthStage('login'));

  $('#invite-guest-enter')?.addEventListener('click', () => {
    enterInviteGuest().catch((error) => setAuthError(error.message || 'Не удалось открыть группу'));
  });

  $$('[data-auth-back]').forEach((button) => button.addEventListener('click', () => setAuthStage(authBackStage())));

  $$('[data-guest-panel]').forEach((button) => button.addEventListener('click', () => {
    const panel = button.dataset.guestPanel;
    $$('[data-guest-panel]').forEach((b) => b.classList.toggle('active', b === button));
    const group = $('#auth-group-discovery');
    const teacher = $('#auth-teacher-discovery');
    if (group) group.hidden = panel !== 'group';
    if (teacher) teacher.hidden = panel !== 'teacher';
    if (panel === 'teacher') $('#auth-teacher-search-input')?.focus();
    else $('#auth-group-search-input')?.focus();
  }));

  $('#telegram-login')?.addEventListener('click', startTelegramAuth);

  $('#invite-telegram-login')?.addEventListener('click', startTelegramAuth);

  $('#register-telegram-login')?.addEventListener('click', startTelegramAuth);

  $('#login-form').addEventListener('submit', (event) => { event.preventDefault(); authenticate('/auth/login', event.currentTarget); });

  $('#register-form').addEventListener('submit', (event) => { event.preventDefault(); authenticate('/auth/register', event.currentTarget); });

  $('#forgot-password')?.addEventListener('click', () => {
    const defaultAccount = $('#login-form input[name="email"]')?.value || '';
    openDialog('Восстановление пароля', `<form class="form" id="forgot-form"><p class="muted">Укажите email или username аккаунта. Если к нему привязан Telegram-бот kUni, бот пришлёт кнопку для смены пароля.</p><label>Email или username<input name="account" autocomplete="username" required value="${escapeHTML(defaultAccount)}" placeholder="name@example.com или username"></label><p class="muted">Если бот не был привязан, автоматическое восстановление пока недоступно. Ответ формы всегда одинаковый — мы не раскрываем существование аккаунта.</p></form>`, [
      {label:'Отмена',action:closeDialog},
      {label:'Отправить в Telegram',primary:true,action:async()=>{const form=$('#forgot-form');if(!form.reportValidity())return;await api('/auth/password/forgot',{method:'POST',body:JSON.stringify({account:form.account.value.trim()})},false);closeDialog();toast('Если аккаунт существует и бот привязан, запрос уже в Telegram');}},
    ]);
  });

  $('#dialog')?.addEventListener('cancel',(event)=>{if($('#dialog').dataset.securityLocked==='true')event.preventDefault();});
}
